import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../config/apiConfig";

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrencyState] = useState(() => {
    return localStorage.getItem("rabbitai_selected_currency") || "INR";
  });
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState({});
  const [lastUpdated, setLastUpdated] = useState("");
  const [provider, setProvider] = useState("ExchangeRate API");
  const [isFallback, setIsFallback] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRates = async (refresh = false) => {
    try {
      const res = await axios.get(`${API_BASE}/api/currency/rates?refresh=${refresh}`);
      if (res.data && res.data.success) {
        const data = res.data.data;
        setCurrencies(data.currencies || []);
        setRates(data.rates || {});
        setLastUpdated(data.last_updated || "");
        setProvider(data.provider || "ExchangeRate API");
        setIsFallback(data.is_fallback || false);
      }
    } catch (err) {
      console.warn("⚠️ [CURRENCY FETCH WARNING]:", err);
      setIsFallback(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    const interval = setInterval(() => fetchRates(), 3600000);
    return () => clearInterval(interval);
  }, []);

  const setSelectedCurrency = (code) => {
    setSelectedCurrencyState(code);
    localStorage.setItem("rabbitai_selected_currency", code);
  };

  const formatIndianRupee = (num) => {
    if (isNaN(num)) return "₹0.00";
    const amount = Number(num);
    const formatted = amount.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    });
    return `₹${formatted}`;
  };

  const formatPrice = (amountInINR, targetCode = null) => {
    const code = targetCode || selectedCurrency;
    const amount = Number(amountInINR) || 0;

    if (code === "INR") {
      return formatIndianRupee(amount);
    }

    const currInfo = rates[code];
    const rate = currInfo ? currInfo.rate : 0.012;
    const converted = amount * rate;
    const symbol = currInfo ? currInfo.symbol : "$";

    try {
      let formattedVal = "";
      if (["JPY", "KRW", "IDR"].includes(code)) {
        formattedVal = Math.round(converted).toLocaleString();
      } else {
        formattedVal = converted.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      return `${symbol}${formattedVal}`;
    } catch (e) {
      return `${symbol}${converted.toFixed(2)}`;
    }
  };

  const convertPrice = (amountInINR, targetCode = null) => {
    const code = targetCode || selectedCurrency;
    const amount = Number(amountInINR) || 0;
    const currInfo = rates[code] || { rate: 1.0, symbol: "₹", flag: "🇮🇳", name: "Indian Rupee" };
    const rate = currInfo.rate || 1.0;
    const rawConverted = code === "INR" ? amount : amount * rate;
    const formatted = formatPrice(amount, code);

    return {
      baseINR: amount,
      code,
      rate,
      raw: rawConverted,
      formatted,
      symbol: currInfo.symbol,
      flag: currInfo.flag,
      name: currInfo.name
    };
  };

  return (
    <CurrencyContext.Provider
      value={{
        selectedCurrency,
        setSelectedCurrency,
        currencies,
        rates,
        lastUpdated,
        provider,
        isFallback,
        loading,
        fetchRates,
        formatPrice,
        convertPrice
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    return {
      selectedCurrency: "INR",
      setSelectedCurrency: () => { },
      currencies: [],
      rates: {},
      lastUpdated: "",
      provider: "ExchangeRate API",
      isFallback: true,
      loading: false,
      fetchRates: () => { },
      formatPrice: (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`,
      convertPrice: (amount) => ({
        baseINR: amount,
        code: "INR",
        rate: 1.0,
        raw: amount,
        formatted: `₹${Number(amount || 0).toLocaleString("en-IN")}`,
        symbol: "₹",
        flag: "🇮🇳",
        name: "Indian Rupee"
      })
    };
  }
  return context;
};

export default CurrencyContext;
